import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";
import type { LoginInput, RegisterInput, Vendor } from "@/lib/api";
import {
  apiLogin,
  apiRegister,
  clearTokens,
  onAuthFailure,
  setTokens,
} from "@/lib/api";

interface AuthContextValue {
  vendor: Vendor | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (input: RegisterInput) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Drop everything a vendor fetched. The price is public and stays, so it does
 *  not vanish from the page while it refetches. */
function dropVendorQueries(queryClient: QueryClient): void {
  queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== "price" });
}

export function AuthProvider({ children }: { children: ReactNode }): React.ReactElement {
  const [vendor, setVendor] = useState<Vendor | null>(null);
  const queryClient = useQueryClient();

  // Cached queries (order history, order detail) belong to the vendor who
  // fetched them and their keys carry no vendor id. Drop them whenever the
  // session ends, or the next vendor to log in on this tab sees them.
  useEffect(() => {
    onAuthFailure(() => {
      setVendor(null);
      clearTokens();
      dropVendorQueries(queryClient);
    });
  }, [queryClient]);

  const login = useCallback(async (email: string, password: string): Promise<void> => {
    const input: LoginInput = { email, password };
    const result = await apiLogin(input);
    setTokens(result.accessToken, result.refreshToken);
    setVendor(result.vendor);
  }, []);

  const register = useCallback(async (input: RegisterInput): Promise<void> => {
    const result = await apiRegister(input);
    setTokens(result.accessToken, result.refreshToken);
    setVendor(result.vendor);
  }, []);

  const logout = useCallback((): void => {
    clearTokens();
    setVendor(null);
    dropVendorQueries(queryClient);
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({
      vendor,
      isAuthenticated: vendor !== null,
      login,
      register,
      logout,
    }),
    [vendor, login, register, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
